import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { currentPostAtom, currentSlideAtom, navBarVisibleAtom, initialSlide } from "../atom";
import { getImageList, getGrossImageList, GrossImage, Photo, viewPost } from "../api/postAPI";
import { PostVideo, getVideoList } from "../api/videoAPI";
import AddVideo from "../component/post/modal/AddVideo";
import { Slide } from "../api/slideAPI";
import {
  PostCase,
  getPostCaseList,
  deletePostCase,
  reorderPostCases,
} from "../api/postCaseAPI";
import styled from "styled-components";

import { Breadcrumb, message, Layout, List, Input, Modal, Tooltip, Button, Result, Dropdown, Popconfirm } from "antd";
import { SwapOutlined, LeftOutlined, RightOutlined, CommentOutlined, LinkOutlined, ExportOutlined, FileTextOutlined } from "@ant-design/icons";
import { postComment, putComment, deleteComment } from "../api/commentAPI";
import { OcspstLink, postOcspstLink, deleteOcspstLink } from "../api/postAPI";
import { issuePartnerTicket } from "../api/partnerLinkAPI";
import { getPartnerGuestSession, clearPartnerGuestSession, PartnerGuestSession } from "../api/api";
import PartnerGuestBanner from "../component/common/PartnerGuestBanner";
import { checkUserName } from "../api/authAPI";
import { CommentThread } from "../component/post";
import { BoxStyle, ContentMinHeight } from "../style/styleValue";

import EditorJS from "@editorjs/editorjs";
import { EDITOR_JS_TOOLS } from "../component/write/tools";
import {
  PostViewerSection,
  PostMediaTabPanel,
  SlideInfoPanel,
  SoaInfoPanel,
  PostContentSection,
  AddImage,
  AddSlide,
  Attatchment,
  PostReferences,
  AuthorModal,
  ImageEditModal,
  SlideEditModal,
  AddGrossImage,
  GrossImageEditModal,
  PostCaseSelector,
  PostCaseModal,
  PostCaseOrderModal,
} from "../component/post";

import {
  getAuthors,
  getPostData,
  deletePost,
  setPassword,
  getPostList,
  isSecretPostResponse,
  isPostAccessDenied,
  SECRET_PASSWORD_INVALID,
} from "../api/postAPI";
import { getLoginUser, getAuthorUserList, getUserProfileImg, patchUserCustomSetting } from "../api/user";
import { deletePhoto, deletePhotoList, putPhotoOrderByList } from "../api/photoApi";
import { deleteGrossImage, deleteGrossImageList, putGrossPhotoOrderByList, convertPhotoToGross, convertGrossToPhoto, patchImageData, updateGrossImage } from "../api/postAPI";
import { multiSelectReorder } from "../component/post/modal/mediaMultiSelect";
import { deleteSlide, deleteSlideList, putSlideOrderByList, patchSlideData } from "../api/slideAPI";
import {
  MediaGroup,
  MediaType,
  createMediaGroup,
  deleteMediaGroup,
  getMediaGroups,
  moveMediaToGroup,
  renameMediaGroup,
  setMediaGroupItems,
} from "../api/mediaGroupAPI";
import {
  GroupFilter,
  buildMembership,
  countByGroup,
  visibleIndices,
} from "../component/post/mediaGroup";
import type { MediaGroupBinding } from "../component/post/PostMediaTabPanel";
import { ViewerContext } from "./Viewer";
import { UserCustomSetting, getUserCustomSetting } from "../api/user";
import { getCategoryInfo } from "../api/categoryAPI";

// 편집/추가(Edit·Add Slide/Image/Gross) 버튼 노출 권한.
// - 질환 카테고리(게시판 categoryType === "GENERAL") 글: 아래 역할(관리자/자문위원) 또는 작성자만 노출.
// - 그 외 게시판 글: 접근한 사용자 누구에게나 노출.
const EDIT_PRIVILEGED_ROLES = ["ADMIN_MAIN", "ADMIN_SUB", "ADVISOR", "SPEACIAL"];

const Post = () => {
  const [editor, setEditor] = useState<EditorJS | null>(null);
  const [postReady, setPostReady] = useState(false);
  const [currentPostData, setCurrentPostData] = useState<any>(null);
  const [onAuthors, setOnAuthors] = useState<any[]>([]);
  const [authorList, setAuthorList] = useState<any[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [isAuthorModalOpen, setIsAuthorModalOpen] = useState<boolean>(false);
  const [hasEditAuth, setHasEditAuth] = useState<boolean>(false);
  // 협력 사이트(OCSPST) 게스트 열람 - 읽기 전용, 회원 전용 API 호출 생략
  const [partnerGuest, setPartnerGuest] = useState<PartnerGuestSession | null>(() => getPartnerGuestSession());
  const [partnerGuestExpired, setPartnerGuestExpired] = useState<boolean>(false);
  const [showAddImageModal, setShowAddImageModal] = useState<boolean>(false);
  const [showEditImageModal, setShowEditImageModal] = useState<boolean>(false);
  const [showAddSlideModal, setShowAddSlideModal] = useState<boolean>(false);
  const [showAddGrossImageModal, setShowAddGrossImageModal] = useState<boolean>(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState<boolean>(false);
  // Video 탭은 임시 화면 — 관리자가 Slide 탭의 'Video 탭 보기' 버튼을 눌렀을 때만 노출
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showVideoTab, setShowVideoTab] = useState<boolean>(false);
  const [showEditGrossImageModal, setShowEditGrossImageModal] = useState<boolean>(false);
  const [infoLayout, setInfoLayout] = useState<"row" | "col">("col");
  const [isSecretPost, setIsSecretPost] = useState(false);
  const [secretPassword, setSecretPassword] = useState("");
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);

  const [layoutMode, setLayoutMode] = useState<"vertical" | "horizontal">("horizontal");
  const [contentPanelWidth, setContentPanelWidth] = useState<number>(40);
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const [imageListEditModalOpen, setImageListEditModalOpen] = useState(false);
  const [slideListEditModalOpen, setSlideListEditModalOpen] = useState(false);
  const [grossImageListEditModalOpen, setGrossImageListEditModalOpen] = useState(false);
  const [orderImageList, setOrderImageList] = useState<Photo[]>([]);
  const [orderSlideList, setOrderSlideList] = useState<Slide[]>([]);
  const [orderGrossImageList, setOrderGrossImageList] = useState<GrossImage[]>([]);
  const [orderChangeReqData, setOrderChangeReqData] = useState<any[]>();

  const [imageList, setImageList] = useState<Photo[]>([]);
  const [grossImageList, setGrossImageList] = useState<GrossImage[]>([]);
  const [slides, setSlides] = useState<Slide[]>([]);

  const [cases, setCases] = useState<PostCase[]>([]);
  const [activeCaseId, setActiveCaseId] = useState<number | null>(null); //null이면 전체
  const [postCaseModalOpen, setPostCaseModalOpen] = useState<boolean>(false);
  const [caseOrderModalOpen, setCaseOrderModalOpen] = useState<boolean>(false);
  const [editingCase, setEditingCase] = useState<PostCase | null>(null);

  // 같은 카테고리 내 이전/다음 게시글 이동용 목록
  const [categoryPosts, setCategoryPosts] = useState<{ id: number; title: string; listOrder?: number }[]>([]);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentImage, setCurrentImage] = useState<Photo | null>(null);
  const [currentGrossImage, setCurrentGrossImage] = useState<GrossImage | null>(null);
  const [isSlideFile, setIsSlideFile] = useState<boolean>(false);
  const [isGrossFile, setIsGrossFile] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  // 동영상 탭 — true 이면 isSlideFile/isGrossFile 과 무관하게 동영상을 보여준다
  const [videoList, setVideoList] = useState<PostVideo[]>([]);
  const [currentVideo, setCurrentVideo] = useState<PostVideo | null>(null);
  const [isVideoFile, setIsVideoFile] = useState<boolean>(false);

  // 미디어 그룹(/api/media-groups). SLIDE/PHOTO/GROSS 를 한 번에 받아 종류별로 갈라 쓴다.
  const [mediaGroups, setMediaGroups] = useState<MediaGroup[]>([]);
  const [groupFilters, setGroupFilters] = useState<Record<MediaType, GroupFilter>>({
    SLIDE: null,
    PHOTO: null,
    GROSS: null,
  });

  const toggleLayoutMode = () => {
    setLayoutMode((prev) => (prev === "vertical" ? "horizontal" : "vertical"));
  };

  // ── 좌측 본문 패널 접기/열기 (좌우 배치에서만. 상하 배치에서는 항상 펼침) ──
  const [contentPanelOpen, setContentPanelOpen] = useState<boolean>(true);
  const isContentCollapsed = layoutMode === "horizontal" && !contentPanelOpen;

  // ── Post comment 우측 패널: 접기/열기 + 너비 조절 ──
  const [commentPanelOpen, setCommentPanelOpen] = useState<boolean>(true);
  const [commentUserName, setCommentUserName] = useState<string | null>(null);
  const [commentPanelWidth, setCommentPanelWidth] = useState<number>(360); // px
  const [isCommentResizing, setIsCommentResizing] = useState<boolean>(false);

  const comments = useMemo(
    () => (Array.isArray(currentPostData?.comments) ? currentPostData.comments : []),
    [currentPostData]
  );

  const reloadComments = async () => {
    if (!currentPostData?.id) return;
    const data = await getPostData(currentPostData.id);
    if (data) setCurrentPostData((prev: any) => ({ ...prev, comments: data.comments ?? [] }));
  };

  const handleAddComment = async (content: string) => {
    if (!currentPostData?.id) return;
    const result = await postComment(currentPostData.id, content);
    if (result) await reloadComments();
    else message.error("Comment 등록에 실패했습니다.");
  };

  const handleEditComment = async (commentId: number, content: string) => {
    const result = await putComment(commentId, content);
    if (result) await reloadComments();
    else message.error("Comment 수정에 실패했습니다.");
  };

  const handleDeleteComment = async (commentId: number) => {
    const result = await deleteComment(commentId);
    if (result) await reloadComments();
    else message.error("Comment 삭제에 실패했습니다.");
  };

  useEffect(() => {
    if (partnerGuest) return;
    checkUserName().then((name) => setCommentUserName(name)).catch(() => setCommentUserName(null));
  }, []);

  // ── 실제 증례(OCSPST) 사이트 링크 ──
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState<boolean>(false);
  const [linkInput, setLinkInput] = useState<string>("");
  const [linkSaving, setLinkSaving] = useState<boolean>(false);

  // 링크 등록/수정 권한: ADMIN_MAIN, SPECIAL
  const canManageOcspstLink = useMemo(
    () => ["ADMIN_MAIN", "SPECIAL"].includes(currentUserRole || ""),
    [currentUserRole]
  );
  const ocspstLinks: OcspstLink[] = currentPostData?.ocspstLinks ?? [];

  useEffect(() => {
    if (partnerGuest) return;
    getLoginUser()
      .then((u: any) => setCurrentUserRole(u?.role ?? null))
      .catch(() => setCurrentUserRole(null));
  }, []);

  const openLinkModal = () => {
    setLinkInput("");
    setLinkModalOpen(true);
  };

  const applyOcspstLinks = (links: OcspstLink[]) => {
    setCurrentPostData((prev: any) => ({ ...prev, ocspstLinks: links }));
    setCurrentPost({ ...currentPost, ocspstLinks: links } as any);
  };

  const ocspstErrorMessage = (e: any, fallback: string): string => e?.response?.data?.message || fallback;

  const handleAddOcspstLink = async () => {
    if (!currentPostData?.id) return;
    const value = linkInput.trim();
    if (!value) return;
    setLinkSaving(true);
    try {
      applyOcspstLinks(await postOcspstLink(currentPostData.id, value));
      setLinkInput("");
      message.success("증례 링크를 연결했습니다.");
    } catch (e) {
      message.error(ocspstErrorMessage(e, "증례 링크 연결에 실패했습니다."));
    } finally {
      setLinkSaving(false);
    }
  };

  const handleRemoveOcspstLink = async (link: OcspstLink) => {
    try {
      applyOcspstLinks(await deleteOcspstLink(link.ocspstLinkId));
      message.success("증례 링크를 해제했습니다.");
    } catch (e) {
      message.error(ocspstErrorMessage(e, "증례 링크 해제에 실패했습니다."));
    }
  };

  // 회원은 1회용 티켓을 받아 OCSPST 에 게스트로 들어가게 한다 (OCSPST 회원이 아니어도 열람 가능)
  // 티켓 발급을 기다리는 동안 팝업 차단을 피하려고 새 탭을 먼저 열어두고 주소만 나중에 지정
  const openOcspstSite = async (link: OcspstLink) => {
    if (partnerGuest || !currentPostData?.id) {
      window.open(link.url, "_blank", "noopener,noreferrer");
      return;
    }

    const newTab = window.open("about:blank", "_blank");
    const entryUrl = await issuePartnerTicket(currentPostData.id, link.ocspstLinkId, secretPassword || undefined);
    const target = entryUrl ?? link.url;
    if (newTab) {
      newTab.opener = null;
      newTab.location.replace(target);
    } else {
      window.open(target, "_blank", "noopener,noreferrer");
    }
  };

  const handlePartnerGuestExpired = () => {
    clearPartnerGuestSession();
    setPartnerGuest(null);
    setPartnerGuestExpired(true);
  };

  // 우측 댓글 패널 너비 드래그 조절 (오른쪽 고정이라 커서가 왼쪽으로 갈수록 넓어짐)
  useEffect(() => {
    if (!isCommentResizing) return;
    const onMove = (e: MouseEvent) => {
      const next = window.innerWidth - e.clientX;
      setCommentPanelWidth(Math.min(Math.max(next, 240), 640));
    };
    const onUp = () => setIsCommentResizing(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isCommentResizing]);

  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>({
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  });

  const navBarVisible = useRecoilValue(navBarVisibleAtom);
  const navBarOffset = useMemo(() => (navBarVisible ? 55 : 0), [navBarVisible]);

  // 뷰어 패널 top → 뷰포트 하단까지의 "확정 높이".
  // - 가로(좌우) 배치: 뷰어 패널에 이 높이를 확정으로 줘, editor 가 길어져도 뷰어가 늘어나지 않게 한다.
  // - 우측 댓글 패널: 배치 모드와 무관하게 이 높이로 뷰포트를 채운다.
  //   (ViewerPanel JSX 는 horizontal 일 때만 이 값을 쓰므로, 세로 배치에서 측정해도 뷰어에는 영향 없음)
  const viewerPanelRef = useRef<HTMLDivElement | null>(null);
  const [viewerPanelHeight, setViewerPanelHeight] = useState<number | null>(null);

  useEffect(() => {
    let raf = 0;
    const measure = () => {
      const el = viewerPanelRef.current;
      if (!el) return;
      // 스크롤 0(진입) 기준의 top → 뷰포트 하단까지의 높이. editor 길이와 무관하게 창 높이로만 결정.
      const top = el.getBoundingClientRect().top;
      setViewerPanelHeight(Math.max(window.innerHeight - top, 0));
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    schedule();
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
    };
  }, [layoutMode, navBarOffset, postReady]);

  const param = useParams<{ id?: string; boardId?: string }>();
  const location = useLocation();
  const { state } = location;

  const searchParams = new URLSearchParams(location.search);
  const soaData = useMemo(() => {
    if (state?.soaData) return state.soaData;

    const meetKppNumber = searchParams.get('kppNumber');
    const meetGender = searchParams.get('meetGender');
    const meetAge = searchParams.get('meetAge');
    const meetOrgan = searchParams.get('meetOrgan');
    const meetDiagnosis = searchParams.get('meetDiagnosis');
    const meetOrganization = searchParams.get('meetOrganization');

    const from = searchParams.get('from');
    const soaMode = searchParams.get('soa');
    const hasSoaData = meetKppNumber || meetGender || meetAge || meetOrgan || meetDiagnosis || meetOrganization;

    if (from === 'conference' || soaMode === 'true' || hasSoaData) {
      return {
        meetKppNumber: meetKppNumber || undefined,
        meetGender: meetGender || undefined,
        meetAge: meetAge || undefined,
        meetOrgan: meetOrgan || undefined,
        meetDiagnosis: meetDiagnosis || undefined,
        meetOrganization: meetOrganization || undefined,
      };
    }
    return null;
  }, [state, location.search]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const filteredImageList = useMemo(() => {
    if (activeCaseId === null) return imageList;
    return imageList.filter((p) => p.postCaseId === activeCaseId);
  }, [imageList, activeCaseId]);

  const filteredGrossImageList = useMemo(() => {
    if (activeCaseId === null) return grossImageList;
    return grossImageList.filter((g) => g.postCaseId === activeCaseId);
  }, [grossImageList, activeCaseId]);

  const filteredSlides = useMemo(() => {
    if (activeCaseId === null) return slides;
    return slides.filter((s: any) => s.postCaseId === activeCaseId);
  }, [slides, activeCaseId]);

  const filteredVideoList = useMemo(() => {
    if (activeCaseId === null) return videoList;
    return videoList.filter((v) => v.postCaseId === activeCaseId);
  }, [videoList, activeCaseId]);

  const handleSetPassword = async (password: string) => {
    if (!currentPostData?.id) return;
    const result = await setPassword(currentPostData.id, true, password);
    if (result.success) {
      message.success("게시글이 비밀글로 설정되었습니다.");
      setCurrentPostData({ ...currentPostData, isSecret: true });
      setCurrentPost({ ...currentPost, isSecret: true });
    } else {
      message.error(result.message);
    }
  };

  const handleRemovePassword = async () => {
    if (!currentPostData?.id) return;
    if (window.confirm("비밀번호를 해제하고 공개글로 전환하시겠습니까?")) {
      const result = await setPassword(currentPostData.id, false);
      if (result.success) {
        message.success("공개글로 전환되었습니다.");
        setCurrentPostData({ ...currentPostData, isSecret: false });
        setCurrentPost({ ...currentPost, isSecret: false });
      } else {
        message.error(result.message);
      }
    }
  };

  const handleSecretSubmit = () => {
    const isValidPassword = /^\d{4}$/.test(secretPassword);
    if (!isValidPassword) {
      message.warning("비밀번호는 4자리 숫자로 입력해주세요.");
      return;
    }
    fetchPost(secretPassword);
  };

  const handleSecretCancel = () => navigate(-1);

  const { id, boardId } = param;
  const postId = id ?? boardId;

  const fetchPostCases = async (resolvedPostId: number) => {
    const list = await getPostCaseList(resolvedPostId);
    setCases(list);
    return list;
  };

  const refreshMedia = async (resolvedPostId: number) => {
    const [images, grossImages] = await Promise.all([
      getImageList(resolvedPostId),
      getGrossImageList(resolvedPostId),
    ]);
    setImageList(images);
    setGrossImageList(grossImages);
    const data = await getPostData(resolvedPostId);
    const nextSlides: Slide[] = data?.slides ?? [];
    if (data?.slides) setSlides(nextSlides);
    // 호출부가 state 업데이트를 기다리지 않고 최신 목록을 바로 쓸 수 있도록 반환
    return { images, grossImages, slides: nextSlides };
  };

  /**
   * Edit 모달은 열릴 때 목록 스냅샷(orderXxxList)을 잡아두고, OK 시 그 스냅샷을 그대로
   * setSlides/setImageList 로 되돌려쓴다. 모달을 연 채 그룹·Case 를 바꾸면 스냅샷이 낡아
   * (1) 모달 안 표시가 갱신되지 않고 (2) OK 를 누르는 순간 방금 바꾼 값이 되돌아간다.
   * → 드래그로 잡아둔 순서는 유지한 채, 각 항목만 최신 데이터로 갈아끼운다.
   */
  const syncOrderSnapshots = (
    nextSlides: Slide[],
    images: Photo[],
    grossImages: GrossImage[]
  ) => {
    setOrderSlideList((prev) =>
      prev.map((it) => nextSlides.find((s) => s.slideId === it.slideId) ?? it)
    );
    setOrderImageList((prev) =>
      prev.map((it) => images.find((p) => p.photoId === it.photoId) ?? it)
    );
    setOrderGrossImageList((prev) =>
      prev.map((it) => grossImages.find((g) => g.grossPhotoId === it.grossPhotoId) ?? it)
    );
  };

  const fetchPost = async (password?: string) => {
    if (!postId) return;
    try {
      const data = await getPostData(+postId, password);
      if (isSecretPostResponse(data)) {
        setAccessDeniedMessage(null);
        setIsSecretPost(true);
        if (data.resultCode === SECRET_PASSWORD_INVALID) message.error("비밀번호가 일치하지 않습니다.");
        return;
      }
      // 게시판/카테고리 권한 부족은 비밀번호 모달이 아니라 접근 권한 없음 화면으로 처리
      if (isPostAccessDenied(data)) {
        // 게스트 토큰 만료 (서버 시간 기준)
        if (partnerGuest && data?.resultCode === "401") {
          handlePartnerGuestExpired();
          return;
        }
        setIsSecretPost(false);
        setCurrentPostData(null);
        setAccessDeniedMessage(data?.message || "해당 게시글에 접근 권한이 없습니다.");
        return;
      }
      setAccessDeniedMessage(null);
      setIsSecretPost(false);
      setCurrentPostData(data);
      setCurrentPost(data);

      if (data) {
        setSlides(data?.slides || []);
        setCurrentSlide(data?.slides?.[0]);

        setMediaGroups(await getMediaGroups(+postId));

        const resolvedPostId = data?.id || +postId;
        const [images, grossImages, videos] = await Promise.all([
          getImageList(resolvedPostId),
          getGrossImageList(resolvedPostId),
          getVideoList(resolvedPostId),
        ]);
        setImageList(images);
        setCurrentImage(images[0] || null);
        setGrossImageList(grossImages);
        setCurrentGrossImage(grossImages[0] || null);
        setVideoList(videos);
        setCurrentVideo(videos[0] || null);
        setIsVideoFile(false);

        // 최초 진입 기본값: 전체(null). (Case 목록은 셀렉터 표시를 위해 로드만 한다)
        await fetchPostCases(resolvedPostId);
        setActiveCaseId(null);

        if (data?.slides?.length > 0) {
          setIsSlideFile(true);
          setIsGrossFile(false);
          setCurrentSlide(data.slides[0]);
        } else if (images.length > 0) {
          setIsSlideFile(false);
          setIsGrossFile(false);
          setCurrentImage(images[0]);
        } else if (grossImages.length > 0) {
          setIsSlideFile(false);
          setIsGrossFile(true);
          setCurrentGrossImage(grossImages[0]);
        } else if (showVideoTab && videos.length > 0) {
          setIsSlideFile(false);
          setIsGrossFile(false);
          setIsVideoFile(true);
          setCurrentVideo(videos[0]);
        } else {
          setIsSlideFile(true);
          setIsGrossFile(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch post:", error);
    }
  };

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const container = document.getElementById("post-layout-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      const clampedWidth = Math.min(Math.max(newWidth, 20), 70);
      setContentPanelWidth(clampedWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing]);

  useEffect(() => {
    fetchPost();
  }, [param]);

  useEffect(() => {
    if (partnerGuest) return;
    const loadUserSettings = async () => {
      try {
        const userSet = await getUserCustomSetting();
        if (userSet) setUserCustomSet(userSet);
      } catch (error) {
        console.error("Failed to load user custom settings:", error);
      }
    };
    loadUserSettings();
    const { id, boardId } = param;
    viewPost(Number(id ?? boardId));
  }, []);

  useEffect(() => {
    const isInitialLoad = Object.keys(userCustomSet).length === 0;
    if (isInitialLoad) return;

    const timer = setTimeout(async () => {
      try {
        await patchUserCustomSetting(userCustomSet);
      } catch (error) {
        console.error("Failed to auto-save user custom settings:", error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [userCustomSet]);

  useEffect(() => {
    const resolveEditAuth = async () => {
      if (!currentPostData) return;
      if (partnerGuest) {
        setHasEditAuth(false);
        return;
      }

      // 게시글이 속한 카테고리(게시판)의 타입 확인.
      // 우선 post 데이터에 있으면 사용, 없으면 카테고리 API 로 조회.
      let categoryType: string | undefined = currentPostData?.categoryType;
      if (!categoryType && currentPostData?.categoryId) {
        try {
          const node = await getCategoryInfo(currentPostData.categoryId);
          categoryType = node?.categoryType;
        } catch (e) {
          console.error("Failed to resolve category type for edit-auth:", e);
        }
      }
      const isGeneralCategory = categoryType === "GENERAL";

      // 질환 카테고리(GENERAL)가 아닌 글: 접근만 하면 편집/추가 버튼 노출
      if (!isGeneralCategory) {
        setHasEditAuth(true);
        return;
      }

      // 질환 카테고리(GENERAL) 글: 관리자/자문위원 또는 작성자만
      const data = await getLoginUser();
      if (data) {
        const privileged = EDIT_PRIVILEGED_ROLES.includes(data.role);
        const isWriter = data.id === currentPostData.writerId;
        setHasEditAuth(privileged || isWriter);
      } else {
        setHasEditAuth(false);
      }
    };
    resolveEditAuth();
  }, [currentPostData]);

  useEffect(() => {
    let cancelled = false;
    getLoginUser().then((user) => {
      if (cancelled) return;
      setIsAdmin(user?.role === "ADMIN_MAIN" || user?.role === "ADMIN_SUB");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (partnerGuest) return;
    const fetchAuthorList = async () => {
      const data = await getAuthorUserList();
      const urls: Record<string, string> = {};
      await Promise.all(
        data.map(async (author: any) => {
          try {
            if (!author.profileImgPath) return;
            const blobData = await getUserProfileImg(author.profileImgPath);
            const thumbData = window.URL.createObjectURL(blobData && blobData);
            urls[author.name] = thumbData;
          } catch (e) {
            console.error(`Failed to load avatar for ${author.name}`, e);
          }
        })
      );
      setAvatarUrls(urls);
      if (!data) return;
      setAuthorList(data);
    };
    fetchAuthorList();

    return () => {
      Object.values(avatarUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    const fetchPostAuthors = async () => {
      if (partnerGuest) return;
      const data = await getAuthors({ postId: currentPostData?.id });
      if (!data) return;
      setOnAuthors(data);
    };
    fetchPostAuthors();
  }, [currentPostData]);

  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentPost?.slides) setSlides(currentPost.slides);
    if (currentPost?.id) {
      const loadMedia = async () => {
        const [images, grossImages] = await Promise.all([
          getImageList(currentPost.id),
          getGrossImageList(currentPost.id),
        ]);
        setImageList(images);
        setGrossImageList(grossImages);
      };
      loadMedia();
    }
  }, [currentPost]);

  const clickEditBtn = () => {
    if (!hasEditAuth) {
      window.alert("편집 권한이 없습니다.");
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const params = new URLSearchParams();

    params.set('isEdit', 'true');
    params.set('postTitle', currentPost.title || '제목 없음');
    params.set('postId', String(currentPost.id));

    const from = searchParams.get('from');
    if (from) {
      params.set('from', from);
      if (from === 'gallery') {
        const name = searchParams.get('name');
        const categoryId = searchParams.get('categoryId');
        if (name) params.set('name', name);
        if (categoryId) params.set('categoryId', categoryId);
      } else if (from === 'portal') {
        const category = searchParams.get('category');
        const categoryName = searchParams.get('categoryName');
        const subCategoryName = searchParams.get('subCategoryName');
        if (category) params.set('category', category);
        if (categoryName) params.set('categoryName', categoryName);
        if (subCategoryName) params.set('subCategoryName', subCategoryName);
      } else if (from === 'conference') {
        const kppNumber = searchParams.get('kppNumber');
        const page = searchParams.get('page');
        if (kppNumber) params.set('kppNumber', kppNumber);
        if (page) params.set('page', page);
        if (soaData) {
          if (soaData.meetGender) params.set('meetGender', soaData.meetGender);
          if (soaData.meetAge) params.set('meetAge', soaData.meetAge);
          if (soaData.meetOrgan) params.set('meetOrgan', soaData.meetOrgan);
          if (soaData.meetDiagnosis) params.set('meetDiagnosis', soaData.meetDiagnosis);
          if (soaData.meetOrganization) params.set('meetOrganization', soaData.meetOrganization);
        }
      } else if (from === 'search') {
        const title = searchParams.get('title');
        if (title) params.set('title', title);
      } else if (from === 'admin') {
        const board = searchParams.get('board');
        const boardName = searchParams.get('boardName');
        const main = searchParams.get('main');
        const mainName = searchParams.get('mainName');
        const sub = searchParams.get('sub');
        const subName = searchParams.get('subName');
        if (board) params.set('board', board);
        if (boardName) params.set('boardName', boardName);
        if (main) params.set('main', main);
        if (mainName) params.set('mainName', mainName);
        if (sub) params.set('sub', sub);
        if (subName) params.set('subName', subName);
      }
    }

    const board = searchParams.get('board');
    if (board) {
      params.set('board', board);
      if (!from) params.set('from', 'dedicated');
      const categoryName = searchParams.get('categoryName');
      const category = searchParams.get('category');
      if (categoryName) params.set('categoryName', categoryName);
      if (category) params.set('category', category);
    }

    const queryString = params.toString();
    const writeUrl = queryString ? `/write?${queryString}` : '/write?isEdit=true';

    if (soaData) {
      navigate(writeUrl, {
        state: {
          conferenceMode: true,
          conferenceEditMode: true,
          rootCategoryId: currentPost.categoryId,
          postData: currentPost,
          conferenceInitialValues: soaData
        },
        replace: false
      });
    } else {
      navigate(writeUrl, { state: { postData: currentPost }, replace: false });
    }
  };

  const clickDeleteBtn = async () => {
    if (!hasEditAuth) {
      window.alert("삭제 권한이 없습니다.");
      return;
    }
    if (window.confirm(`${currentPost.title} - 해당 게시글을 삭제하시겠습니까?`)) {
      await deletePost(currentPost.id);
      navigate(-1);
    }
  };

  const handleSelectCase = (postCaseId: number | null) => {
    setActiveCaseId(postCaseId);
    setCurrentIndex(0);
  };

  const handleAddCase = () => {
    setEditingCase(null);
    setPostCaseModalOpen(true);
  };

  const handleEditCase = (pc: PostCase) => {
    setEditingCase(pc);
    setPostCaseModalOpen(true);
  };

  const handleDeleteCase = async (pc: PostCase) => {
    if (!window.confirm(`해당 Case 를 삭제하시겠습니까?\n포함된 슬라이드/이미지는 미배정 상태로 돌아갑니다.`)) return;
    const result = await deletePostCase({
      postCaseId: pc.postCaseId,
      organ: pc.organ,
      note: pc.note,
      gender: pc.gender,
      age: pc.age,
    });
    if (result.success) {
      message.success("Case 가 삭제되었습니다.");
      if (activeCaseId === pc.postCaseId) {
        setActiveCaseId(null);  // 전체로
      }
      if (currentPostData?.id) {
        await fetchPostCases(currentPostData.id);
        await refreshMedia(currentPostData.id);
      }
    } else {
      message.error(result.message);
    }
  };

  const handleCaseModalSuccess = async () => {
    if (currentPostData?.id) {
      await fetchPostCases(currentPostData.id);
    }
  };

  // Case 순서 변경 — 드래그로 정해진 순서대로 listOrder 를 1부터 다시 매겨 한 번에 저장
  const handleReorderCases = async (ordered: PostCase[]) => {
    const payload = ordered.map((pc, i) => ({
      postCaseId: pc.postCaseId,
      listOrder: i + 1,
    }));
    const result = await reorderPostCases(payload);
    if (!result.success) {
      message.error(result.message || "Case 순서 변경에 실패했습니다.");
      return;
    }
    if (currentPostData?.id) await fetchPostCases(currentPostData.id);
    setCaseOrderModalOpen(false);
    message.success("Case 순서가 변경되었습니다.");
  };

  useEffect(() => {
    setIsVideoFile(false);
    if (filteredSlides.length > 0) {
      setIsSlideFile(true);
      setIsGrossFile(false);
      setCurrentSlide(filteredSlides[0]);
      setCurrentIndex(0);
    } else if (filteredImageList.length > 0) {
      setIsSlideFile(false);
      setIsGrossFile(false);
      setCurrentImage(filteredImageList[0]);
      setCurrentIndex(0);
    } else if (filteredGrossImageList.length > 0) {
      setIsSlideFile(false);
      setIsGrossFile(true);
      setCurrentGrossImage(filteredGrossImageList[0]);
      setCurrentIndex(0);
    } else if (showVideoTab && filteredVideoList.length > 0) {
      setIsSlideFile(false);
      setIsGrossFile(false);
      setIsVideoFile(true);
      setCurrentVideo(filteredVideoList[0]);
      setCurrentIndex(0);
    } else {
      setCurrentIndex(0);
      setCurrentImage(null);
      setCurrentGrossImage(null);
    }
  }, [activeCaseId]);

  const handleCaseAssigned = async () => {
    if (!currentPostData?.id) return;
    // refreshMedia 가 돌려주는 최신 목록으로 스냅샷을 갱신한다.
    // (state 변수는 이 렌더 시점의 낡은 값이라 그대로 쓰면 갱신이 한 박자 밀린다)
    const media = await refreshMedia(currentPostData.id);
    syncOrderSnapshots(media.slides, media.images, media.grossImages);
  };

  const handleSlideUpdate = async () => {
    if (!currentPostData?.id) return;
  
    try {
      const [data, groups] = await Promise.all([
        getPostData(currentPostData.id),
        getMediaGroups(currentPostData.id),
      ]);

      const nextSlides: Slide[] = data?.slides ?? [];
      setSlides(nextSlides);
      setMediaGroups(groups);
  
      if (currentSlide?.slideId) {
        const refreshed = nextSlides.find(
          (s) => s.slideId === currentSlide.slideId
        );
        if (refreshed) {
          setCurrentSlide(refreshed);
        }
      }
  
      if (data) setCurrentPost(data);
    } catch (e) {
      console.error("Failed to refresh slides:", e);
    }
  };

  // 썸네일 우클릭 → 이름 수정. index 는 각 목록(filtered*) 기준.
  const handleRenameSlide = async (index: number, newName: string) => {
    const slide = filteredSlides[index] as any;
    if (!slide?.slideId) return;
    try {
      // 다른 정보가 지워지지 않도록 기존 값 유지 + slidename 만 교체
      await patchSlideData({
        slideId: slide.slideId,
        slidename: newName,
        caption: slide.caption,
        gender: slide.gender,
        age: slide.age,
        site: slide.site,
        zindanName: slide.zindanName,
        description: slide.description,
      });
      await handleSlideUpdate();
      message.success("슬라이드 이름이 수정되었습니다.");
    } catch (e) {
      console.error(e);
      message.error("이름 수정에 실패했습니다.");
    }
  };

  const handleRenameImage = async (index: number, newName: string) => {
    const photo = filteredImageList[index];
    if (!photo) return;
    try {
      await patchImageData({ ...photo, imageName: newName });
      if (currentPost?.id) {
        const images = await getImageList(currentPost.id);
        setImageList(images);
        if (currentImage?.photoId === photo.photoId) {
          const fresh = images.find((p: Photo) => p.photoId === photo.photoId);
          if (fresh) setCurrentImage(fresh);
        }
      }
      message.success("이미지 이름이 수정되었습니다.");
    } catch (e) {
      console.error(e);
      message.error("이름 수정에 실패했습니다.");
    }
  };

  const handleRenameGross = async (index: number, newName: string) => {
    const gross = filteredGrossImageList[index];
    if (!gross) return;
    try {
      // gross 는 caption 이 이름 역할
      await updateGrossImage({ ...gross, caption: newName });
      if (currentPost?.id) {
        const grossImages = await getGrossImageList(currentPost.id);
        setGrossImageList(grossImages);
        if (currentGrossImage?.grossPhotoId === gross.grossPhotoId) {
          const fresh = grossImages.find((g: GrossImage) => g.grossPhotoId === gross.grossPhotoId);
          if (fresh) setCurrentGrossImage(fresh);
        }
      }
      message.success("이미지 이름이 수정되었습니다.");
    } catch (e) {
      console.error(e);
      message.error("이름 수정에 실패했습니다.");
    }
  };

  // 그룹 필터가 걸린 뒤 실제로 보이는 원본 인덱스들. 이전/다음 이동 기준이며,
  // 슬라이드뿐 아니라 이미지·그로스도 동일하게 그룹 필터를 따른다.
  const filteredSlideIndices = useMemo(
    () => visibleIndices(filteredSlides, groupFilters.SLIDE),
    [filteredSlides, groupFilters.SLIDE]
  );
  const filteredImageIndices = useMemo(
    () => visibleIndices(filteredImageList, groupFilters.PHOTO),
    [filteredImageList, groupFilters.PHOTO]
  );
  const filteredGrossIndices = useMemo(
    () => visibleIndices(filteredGrossImageList, groupFilters.GROSS),
    [filteredGrossImageList, groupFilters.GROSS]
  );

  // 동영상은 그룹 필터가 없어 case 필터 목록 전체가 이동 대상
  const filteredVideoIndices = useMemo(
    () => filteredVideoList.map((_, i) => i),
    [filteredVideoList]
  );

  const activeIndices = isVideoFile
    ? filteredVideoIndices
    : isSlideFile
    ? filteredSlideIndices
    : isGrossFile
    ? filteredGrossIndices
    : filteredImageIndices;

  const selectByType = (index: number) => {
    if (isVideoFile) handleVideoSelect(index);
    else if (isSlideFile) handleSlideSelect(index);
    else if (isGrossFile) handleGrossImageSelect(index);
    else handleImageSelect(index);
  };

  const handlePrev = () => {
    const pos = activeIndices.indexOf(currentIndex);
    if (pos > 0) selectByType(activeIndices[pos - 1]);
  };

  const handleNext = () => {
    const pos = activeIndices.indexOf(currentIndex);
    if (pos !== -1 && pos < activeIndices.length - 1) selectByType(activeIndices[pos + 1]);
  };

  // 현재 게시글이 속한 카테고리의 게시글 목록을 불러와 이전/다음 이동 기준으로 사용.
  // 카테고리 페이지(Category)와 동일하게 listOrder 오름차순으로 정렬한다.
  useEffect(() => {
    const catId = currentPostData?.categoryId;
    if (!catId) {
      setCategoryPosts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await getPostList(1, 9999, catId, "id", "DESC");
        if (cancelled) return;
        const sorted = Array.isArray(list) ? [...list] : [];
        sorted.sort((a: any, b: any) => {
          const lo = (a.listOrder ?? 0) - (b.listOrder ?? 0);
          return lo !== 0 ? lo : a.id - b.id;
        });
        setCategoryPosts(sorted);
      } catch (e) {
        console.error("Failed to load category posts for prev/next nav:", e);
        if (!cancelled) setCategoryPosts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPostData?.categoryId]);

  const { prevPost, nextPost } = useMemo(() => {
    const idx = categoryPosts.findIndex((p) => p.id === currentPostData?.id);
    if (idx === -1) return { prevPost: null, nextPost: null };
    return {
      prevPost: idx > 0 ? categoryPosts[idx - 1] : null,
      nextPost: idx < categoryPosts.length - 1 ? categoryPosts[idx + 1] : null,
    };
  }, [categoryPosts, currentPostData?.id]);

  // 같은 카테고리 내 이동이므로 현재 쿼리(breadcrumb 출처)를 그대로 유지한다.
  const goToPost = (targetId: number) => {
    navigate(`/post/${targetId}${location.search}`);
    window.scrollTo(0, 0);
  };

  const handleImageSelect = (index: number) => {
    setIsVideoFile(false);
    setIsSlideFile(false);
    setIsGrossFile(false);
    setCurrentImage(filteredImageList.length === 0 ? null : filteredImageList[index]);
    setCurrentIndex(index);
  };

  const handleSlideSelect = (index: number) => {
    setIsVideoFile(false);
    setIsSlideFile(true);
    setIsGrossFile(false);
    setCurrentSlide(filteredSlides.length === 0 ? initialSlide : filteredSlides[index]);
    setCurrentIndex(index);
  };

  const handleGrossImageSelect = (index: number) => {
    setIsVideoFile(false);
    setIsSlideFile(false);
    setIsGrossFile(true);
    setCurrentGrossImage(filteredGrossImageList.length === 0 ? null : filteredGrossImageList[index]);
    setCurrentIndex(index);
  };

  const handleVideoSelect = (index: number) => {
    setIsVideoFile(true);
    setIsSlideFile(false);
    setIsGrossFile(false);
    setCurrentVideo(filteredVideoList[index] ?? null);
    setCurrentIndex(index);
  };

  const reloadVideoList = async () => {
    if (!currentPostData?.id) return;
    const videos = await getVideoList(currentPostData.id);
    setVideoList(videos);
  };

  const handleEditImage = () => {
    if (!currentImage) return;
    setShowEditImageModal(true);
  };

  const handleEditGrossImage = () => {
    if (!currentGrossImage) return;
    setShowEditGrossImageModal(true);
  };

  const handleOrder = async (mode: "image" | "slide" | "gross") => {
    try {
      if (mode === "image" && orderImageList) setImageList(orderImageList);
      if (mode === "slide" && orderSlideList) setSlides(orderSlideList);
      if (mode === "gross" && orderGrossImageList) setGrossImageList(orderGrossImageList);

      if (orderChangeReqData && orderChangeReqData.length > 0) {
        if (mode === "image") await putPhotoOrderByList(orderChangeReqData);
        else if (mode === "slide") await putSlideOrderByList(orderChangeReqData);
        else if (mode === "gross") await putGrossPhotoOrderByList(orderChangeReqData);
      }

      if (mode === "image") closeImageEditModal();
      else if (mode === "slide") closeSlideEditModal();
      else closeGrossImageEditModal();
    } catch (err) {
      console.error({ err });
    }
  };

  const handleOnDragEnd = (
    result: any,
    mode: "image" | "slide" | "gross",
    selectedIds: number[] = []
  ) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (mode === "image") {
      const reordered = multiSelectReorder(
        orderImageList,
        (it) => it.photoId,
        selectedIds,
        source.index,
        destination.index
      );
      setOrderImageList(reordered);
      setOrderChangeReqData(
        reordered.map((item, i) => ({ listOrder: i + 1, photoId: item.photoId }))
      );
    } else if (mode === "slide") {
      const reordered = multiSelectReorder(
        orderSlideList,
        (it) => it.slideId,
        selectedIds,
        source.index,
        destination.index
      );
      setOrderSlideList(reordered);
      setOrderChangeReqData(
        reordered.map((item, i) => ({ listOrder: i + 1, slideId: item.slideId }))
      );
    } else {
      const reordered = multiSelectReorder(
        orderGrossImageList,
        (it) => it.grossPhotoId,
        selectedIds,
        source.index,
        destination.index
      );
      setOrderGrossImageList(reordered);
      setOrderChangeReqData(
        reordered.map((item, i) => ({ listOrder: i + 1, grossPhotoId: item.grossPhotoId }))
      );
    }
  };

  const openImageEditModal = () => {
    setOrderImageList(filteredImageList);
    setImageListEditModalOpen(true);
  };
  const closeImageEditModal = () => setImageListEditModalOpen(false);

  const openSlideEditModal = () => {
    setOrderSlideList(filteredSlides);
    setSlideListEditModalOpen(true);
  };
  const closeSlideEditModal = () => setSlideListEditModalOpen(false);

  const openGrossImageEditModal = () => {
    setOrderGrossImageList(filteredGrossImageList);
    setGrossImageListEditModalOpen(true);
  };
  const closeGrossImageEditModal = () => setGrossImageListEditModalOpen(false);

  /* ============================ 미디어 그룹 ============================ */

  // 그룹 목록 + 미디어 목록을 함께 새로고침.
  // 그룹 소속은 미디어 응답에 실려오므로 그룹만 다시 받으면 화면이 어긋난다.
  const reloadGroupsAndMedia = async () => {
    const resolvedId = currentPostData?.id ?? (postId ? +postId : null);
    if (!resolvedId) return;
    const [groups, media] = await Promise.all([
      getMediaGroups(resolvedId),
      refreshMedia(resolvedId),
    ]);
    setMediaGroups(groups);
    // Edit 모달이 열린 채 그룹을 바꾼 경우, 모달이 들고 있는 스냅샷도 함께 갱신한다
    syncOrderSnapshots(media.slides, media.images, media.grossImages);
  };

  // 종류별 미디어 목록 (case 필터까지 적용된 현재 화면 기준)
  const mediaListOf = (type: MediaType): any[] =>
    type === "SLIDE" ? filteredSlides : type === "PHOTO" ? filteredImageList : filteredGrossImageList;

  const handleCreateGroup = async (type: MediaType, name: string) => {
    const resolvedId = currentPostData?.id ?? (postId ? +postId : null);
    if (!resolvedId) return;
    await createMediaGroup({ postId: resolvedId, mediaType: type, name });
    await reloadGroupsAndMedia();
    message.success("그룹이 생성되었습니다.");
  };

  const handleRenameGroup = async (mediaGroupId: number, name: string) => {
    await renameMediaGroup(mediaGroupId, name);
    await reloadGroupsAndMedia();
    message.success("그룹 이름이 수정되었습니다.");
  };

  const handleDeleteGroup = async (type: MediaType, mediaGroupId: number) => {
    await deleteMediaGroup(mediaGroupId);
    // 삭제한 그룹을 보고 있었다면 전체로 되돌린다
    setGroupFilters((prev) =>
      prev[type] === mediaGroupId ? { ...prev, [type]: null } : prev
    );
    await reloadGroupsAndMedia();
    message.success("그룹이 삭제되었습니다.");
  };

  /**
   * 선택한 미디어들을 그룹으로 이동/해제. newGroupName 이 오면 그 이름으로 새 그룹을 만들면서 배정한다.
   * (items 교체 API 는 다른 그룹 소속 미디어를 받지 않으므로 moveMediaToGroup 이 원 그룹에서 먼저 뺀다)
   */
  const handleMoveToGroup = async (
    type: MediaType,
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => {
    const resolvedId = currentPostData?.id ?? (postId ? +postId : null);
    if (!resolvedId || mediaIds.length === 0) return;

    // 소속 맵은 case 필터와 무관하게 게시글 전체 미디어 기준이어야 한다
    // (다른 case 의 미디어가 같은 그룹에 있으면 교체 PUT 에서 빠져버리므로)
    const allOfType = type === "SLIDE" ? slides : type === "PHOTO" ? imageList : grossImageList;
    const membership = buildMembership(allOfType, type);

    if (newGroupName) {
      // 새 그룹 생성 + 배정. 다른 그룹에 속한 미디어는 먼저 빼야 하므로 그룹 해제를 선행한다.
      await moveMediaToGroup(mediaIds, null, membership);
      await createMediaGroup({
        postId: resolvedId,
        mediaType: type,
        name: newGroupName,
        mediaIds,
      });
    } else {
      await moveMediaToGroup(mediaIds, targetGroupId, membership);
    }

    await reloadGroupsAndMedia();
    message.success(targetGroupId == null && !newGroupName ? "그룹이 해제되었습니다." : "그룹이 변경되었습니다.");
  };

  /**
   * 그룹 편집 — 모달에서 체크한 목록이 그 그룹의 새 멤버 전체가 된다.
   * 사용자가 전체 목록을 보고 결정한 경우이므로 교체 API 를 그대로 쓴다 (요청 1회, 서버에서 원자적 처리).
   */
  const handleEditGroupMembers = async (mediaGroupId: number, mediaIds: number[]) => {
    await setMediaGroupItems(mediaGroupId, mediaIds);
    await reloadGroupsAndMedia();
    message.success("그룹이 편집되었습니다.");
  };

  const groupsOf = (type: MediaType) => mediaGroups.filter((g) => g.mediaType === type);

  const groupBindingOf = (type: MediaType): MediaGroupBinding => ({
    groups: groupsOf(type),
    counts: countByGroup(mediaListOf(type)),
    active: groupFilters[type],
    onFilterChange: (next) => setGroupFilters((prev) => ({ ...prev, [type]: next })),
    onCreate: (name) => handleCreateGroup(type, name),
    onRename: handleRenameGroup,
    onDelete: (mediaGroupId) => handleDeleteGroup(type, mediaGroupId),
    onMove: (mediaIds, targetGroupId, newGroupName) =>
      handleMoveToGroup(type, mediaIds, targetGroupId, newGroupName),
    onEditMembers: handleEditGroupMembers,
  });

  const deleteImage = async (photoId: number) => {
    setOrderImageList((prev) => prev.filter((item) => item.photoId !== photoId));
    await deletePhoto(photoId);
  };

  const deleteSlideItem = async (slideId: number) => {
    setOrderSlideList((prev) => prev.filter((item) => item.slideId !== slideId));
    await deleteSlide(slideId);
  };

  const deleteGrossImageItem = async (grossPhotoId: number) => {
    setOrderGrossImageList((prev) =>
      prev.filter((item) => item.grossPhotoId !== grossPhotoId)
    );
    const result = await deleteGrossImage(grossPhotoId);
    if (!result.success) message.error(result.message);
  };

  /* ============================ 다중 선택 일괄 삭제 =========================== */
  const deleteManyImages = async (ids: number[]) => {
    if (ids.length === 0) return;
    setOrderImageList((prev) => prev.filter((item) => !ids.includes(item.photoId!)));
    await deletePhotoList(ids);
  };

  const deleteManySlides = async (ids: number[]) => {
    if (ids.length === 0) return;
    setOrderSlideList((prev) => prev.filter((item) => !ids.includes(item.slideId!)));
    await deleteSlideList(ids);
  };

  const deleteManyGrossImages = async (ids: number[]) => {
    if (ids.length === 0) return;
    setOrderGrossImageList((prev) =>
      prev.filter((item) => !ids.includes(item.grossPhotoId!))
    );
    const result = await deleteGrossImageList(ids);
    if (!result.success) message.error(result.message);
  };

  /* ================= 이미지 <-> 그로스 이미지 변환/이동 (같은 게시글 내) ================= */
  const convertImagesToGross = async (ids: number[]) => {
    if (ids.length === 0) return;
    const result = await convertPhotoToGross(ids);
    setOrderImageList((prev) => prev.filter((item) => !ids.includes(item.photoId!)));
    if (result.success) message.success(`${ids.length}개를 Gross로 이동했습니다.`);
    else message.error(result.message || "Gross로 이동 실패");
    if (currentPostData?.id) await refreshMedia(currentPostData.id);
  };

  const convertGrossToImages = async (ids: number[]) => {
    if (ids.length === 0) return;
    const result = await convertGrossToPhoto(ids);
    setOrderGrossImageList((prev) => prev.filter((item) => !ids.includes(item.grossPhotoId!)));
    if (result.success) message.success(`${ids.length}개를 Image로 이동했습니다.`);
    else message.error(result.message || "Image로 이동 실패");
    if (currentPostData?.id) await refreshMedia(currentPostData.id);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  };

  useEffect(() => {
    const init = async () => {
      editor && editor.destroy();
      // FreeText 본문은 PostContentSection 이 HTML 로 그린다
      if (currentPostData?.contentFormat === "FREETEXT") {
        setEditor(null);
        return;
      }
      let jsonData;
      try {
        jsonData = JSON.parse(currentPostData?.content);
      } catch (e) {
        console.log("JSON parsing error:", e);
        jsonData = undefined;
      }
      const newEditor = new EditorJS({
        holder: "editorJS",
        readOnly: true,
        tools: EDITOR_JS_TOOLS(currentPostData.id),
        minHeight: 0,
        data: jsonData,
      });
      setEditor(newEditor);
    };
    if (postReady) init();
  }, [postReady]);

  useEffect(() => {
    if (currentPostData && currentPostData?.id) setPostReady(true);
  }, [currentPostData]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // 그룹 필터가 걸린 현재 탭의 이동 가능 목록 기준
      if (activeIndices.length === 0) return;

      if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, isSlideFile, isGrossFile, isVideoFile, activeIndices]);

  return (
    <>
      {partnerGuestExpired ? (
        <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Result
            status="info"
            title="게스트 열람 시간이 끝났습니다"
            subTitle="계속 보려면 로그인하거나 회원가입해 주세요. 소아고형암 온라인 자문시스템에서 링크를 다시 누르면 다시 열람할 수 있습니다."
            extra={[
              <Button key="login" onClick={() => navigate("/login")}>
                로그인
              </Button>,
              <Button key="signup" type="primary" onClick={() => navigate("/signup")}>
                회원가입
              </Button>,
            ]}
          />
        </Layout.Content>
      ) : !isSecretPost && currentPostData && currentPostData?.id ? (
        <PostPageWrapper $navBarOffset={navBarOffset}>
          {partnerGuest && (
            <PartnerGuestBanner expiresAt={partnerGuest.expiresAt} onExpire={handlePartnerGuestExpired} />
          )}
          <BreadcrumbWrapper>
            <Breadcrumb />
          </BreadcrumbWrapper>

          {/* 댓글 패널을 항상 우측 컬럼에 두기 위해 PostLayout 을 flex row 로 감싼다.
              → 상하(vertical) 배치에서도 댓글이 뷰어 옆(오른쪽)에 위치한다. */}
          <PageBody>
          <PostLayout
            id="post-layout-container"
            $layoutMode={layoutMode}
            $isResizing={isResizing}
          >
            {isContentCollapsed && (
              <ContentCollapsedTab type="button" onClick={() => setContentPanelOpen(true)} title="본문 열기">
                <RightOutlined />
                <CommentTabLabel>Content</CommentTabLabel>
                <FileTextOutlined />
              </ContentCollapsedTab>
            )}

            {/* 접어도 에디터 상태가 유지되도록 언마운트하지 않고 숨긴다 */}
            <ContentPanel
              $layoutMode={layoutMode}
              $width={contentPanelWidth}
              style={isContentCollapsed ? { display: "none" } : undefined}
            >
              {layoutMode === "horizontal" && (
                <ContentPanelHeader>
                  <CommentPanelTitle>
                    <FileTextOutlined /> Content
                  </CommentPanelTitle>
                  <CommentCollapseBtn type="button" title="본문 접기" onClick={() => setContentPanelOpen(false)}>
                    <LeftOutlined />
                  </CommentCollapseBtn>
                </ContentPanelHeader>
              )}
              <ContentSection>
                {(prevPost || nextPost) && (
                  <PostNavBar>
                    <PostNavButton
                      type="button"
                      onClick={() => prevPost && goToPost(prevPost.id)}
                      disabled={!prevPost}
                    >
                      <LeftOutlined />
                      <PostNavText>
                        <span className="dir">이전 글</span>
                        {prevPost && <span className="title">{prevPost.title || "제목 없음"}</span>}
                      </PostNavText>
                    </PostNavButton>
                    <PostNavButton
                      type="button"
                      className="next"
                      onClick={() => nextPost && goToPost(nextPost.id)}
                      disabled={!nextPost}
                    >
                      <PostNavText className="right">
                        <span className="dir">다음 글</span>
                        {nextPost && <span className="title">{nextPost.title || "제목 없음"}</span>}
                      </PostNavText>
                      <RightOutlined />
                    </PostNavButton>
                  </PostNavBar>
                )}
                <PostMetaContainer>
                  {/* 좌측: 실제 증례(OCSPST) 링크 영역 */}
                  <OcspstArea>
                    {ocspstLinks.length > 0 ? (
                      <>
                        {ocspstLinks.length === 1 ? (
                          <OcspstViewButton
                            type="primary"
                            icon={<ExportOutlined />}
                            onClick={() => openOcspstSite(ocspstLinks[0])}
                          >
                            소아고형암 온라인 자문시스템 실제 증례 보기
                          </OcspstViewButton>
                        ) : (
                          // 연결된 증례가 여러 건이면 골라서 이동
                          <Dropdown
                            trigger={["click"]}
                            menu={{
                              items: ocspstLinks.map((link) => ({
                                key: String(link.ocspstLinkId),
                                label: ocspstCaseLabel(link),
                                icon: <ExportOutlined />,
                                onClick: () => openOcspstSite(link),
                              })),
                            }}
                          >
                            <OcspstViewButton type="primary" icon={<ExportOutlined />}>
                              소아고형암 온라인 자문시스템 실제 증례 보기 ({ocspstLinks.length})
                            </OcspstViewButton>
                          </Dropdown>
                        )}
                        {canManageOcspstLink && (
                          <Tooltip title="증례 링크 추가/해제">
                            <Button size="small" icon={<LinkOutlined />} onClick={openLinkModal} />
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      canManageOcspstLink && (
                        <OcspstLinkButton size="small" icon={<LinkOutlined />} onClick={openLinkModal}>
                          실제 증례와 링크 연결하기
                        </OcspstLinkButton>
                      )
                    )}
                  </OcspstArea>

                  {/* 우측: 작성자 / 최근 수정 */}
                  <MetaRight>
                    <MetaText>작성자: {currentPostData?.writer || "알 수 없음"}</MetaText>
                    {currentPostData?.editHistory && currentPostData.editHistory.length > 0 && (
                      <>
                        <MetaDivider>|</MetaDivider>
                        <Tooltip
                          placement="bottomRight"
                          title={
                            <HistoryList>
                              <div className="history-title">수정 이력</div>
                              {currentPostData.editHistory.map((history: any) => (
                                <div key={history.historyId} className="history-item">
                                  <span className="editor">{history.editorName}</span>
                                  <span className="date">{formatDateTime(history.editedAt)}</span>
                                </div>
                              ))}
                            </HistoryList>
                          }
                        >
                          <MetaText $isLink>
                            최근 수정: {currentPostData.editHistory[0].editorName} ({formatDateTime(currentPostData.editHistory[0].editedAt)})
                          </MetaText>
                        </Tooltip>
                      </>
                    )}
                  </MetaRight>
                </PostMetaContainer>
                <PostContentSection
                  contentFormat={currentPostData?.contentFormat}
                  content={currentPostData?.content}
                  contentEn={currentPostData?.contentEn}
                  title={currentPostData?.title || ""}
                  hasEditAuth={hasEditAuth}
                  onAuthors={onAuthors}
                  avatarUrls={avatarUrls}
                  isSecret={currentPostData?.isSecret || false}
                  onClickEdit={clickEditBtn}
                  onClickDelete={clickDeleteBtn}
                  onClickAuthorEdit={() => setIsAuthorModalOpen(true)}
                  onSetPassword={handleSetPassword}
                  onRemovePassword={handleRemovePassword}
                />
              </ContentSection>

              <AttachmentSection>
                <PostReferences
                  postId={currentPostData.id}
                  referenceText={currentPostData.referenceText}
                  canEdit={hasEditAuth && !partnerGuest}
                />
                <Attatchment postId={currentPostData.id} isDeleteButtonVisible={!partnerGuest} isUploadVisible={!partnerGuest} />
              </AttachmentSection>

              <AuthorModal
                open={isAuthorModalOpen}
                onClose={() => setIsAuthorModalOpen(false)}
                postId={currentPostData?.id}
                onAuthors={onAuthors}
                setOnAuthors={setOnAuthors}
                authorList={authorList}
                avatarUrls={avatarUrls}
              />
            </ContentPanel>

            {layoutMode === "horizontal" && !isContentCollapsed && (
              <ResizeDivider onMouseDown={handleResizeStart} $isResizing={isResizing}>
                <ResizeHandle />
              </ResizeDivider>
            )}

            <ViewerContext.Provider value={{ userCustomSet, setUserCustomSet }}>
              <ViewerPanel
                ref={viewerPanelRef}
                $layoutMode={layoutMode}
                style={
                  layoutMode === "horizontal" && viewerPanelHeight
                    ? { height: `${viewerPanelHeight}px` }
                    : undefined
                }
              >
                <PostCaseSelector
                  cases={cases}
                  activeCaseId={activeCaseId}
                  hasEditAuth={hasEditAuth}
                  onSelectCase={handleSelectCase}
                  onAddCase={handleAddCase}
                  onEditCase={handleEditCase}
                  onDeleteCase={handleDeleteCase}
                  onReorderCases={() => setCaseOrderModalOpen(true)}
                  rightAction={
                    <LayoutToggleButton
                      icon={<SwapOutlined rotate={layoutMode === "vertical" ? 0 : 90} />}
                      size="small"
                      onClick={toggleLayoutMode}
                      title={layoutMode === "vertical" ? "좌우 배치로 전환" : "상하 배치로 전환"}
                    >
                      {layoutMode === "vertical" ? "좌우 배치" : "상하 배치"}
                    </LayoutToggleButton>
                  }
                />

                <PostViewerSection
                  imageList={filteredImageList}
                  slides={filteredSlides}
                  grossImageList={filteredGrossImageList}
                  currentIndex={currentIndex}
                  isSlideFile={isSlideFile}
                  isGrossFile={isGrossFile}
                  currentImage={currentImage}
                  currentSlide={currentSlide}
                  currentGrossImage={currentGrossImage}
                  currentPost={currentPost}
                  hasEditAuth={hasEditAuth}
                  activeCaseId={activeCaseId}
                  layoutMode={layoutMode}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onEditImage={handleEditImage}
                  onEditGrossImage={handleEditGrossImage}
                  videoList={filteredVideoList}
                  isVideoFile={isVideoFile}
                  currentVideo={currentVideo}
                />

                <MediaTabWrapper>
                  <PostMediaTabPanel
                    slides={filteredSlides}
                    imageList={filteredImageList}
                    grossImageList={filteredGrossImageList}
                    currentIndex={currentIndex}
                    isSlideFile={isSlideFile}
                    isGrossFile={isGrossFile}
                    hasEditAuth={hasEditAuth}
                    onSlideSelect={handleSlideSelect}
                    onImageSelect={handleImageSelect}
                    onGrossImageSelect={handleGrossImageSelect}
                    onOpenSlideEditModal={openSlideEditModal}
                    onOpenImageEditModal={openImageEditModal}
                    onOpenGrossImageEditModal={openGrossImageEditModal}
                    onShowAddSlideModal={() => setShowAddSlideModal(true)}
                    onShowAddImageModal={() => setShowAddImageModal(true)}
                    onShowAddGrossImageModal={() => setShowAddGrossImageModal(true)}
                    groupBindings={{
                      SLIDE: groupBindingOf("SLIDE"),
                      PHOTO: groupBindingOf("PHOTO"),
                      GROSS: groupBindingOf("GROSS"),
                    }}
                    slideOnlyMode={false}
                    onRenameSlide={handleRenameSlide}
                    onRenameImage={handleRenameImage}
                    onRenameGross={handleRenameGross}
                    videoList={filteredVideoList}
                    isVideoFile={isVideoFile}
                    onVideoSelect={showVideoTab ? handleVideoSelect : undefined}
                    onShowAddVideoModal={() => setShowAddVideoModal(true)}
                    isVideoTabVisible={showVideoTab}
                    onToggleVideoTab={isAdmin ? () => setShowVideoTab(true) : undefined}
                  />
                </MediaTabWrapper>
                {soaData ? (
                  <>
                    <InfoPanelHeader><span>INFORMATION</span></InfoPanelHeader>
                    <SoaInfoPanel
                      soaData={soaData}
                      layout={infoLayout}
                      hasEditAuth={hasEditAuth}
                      onEdit={clickEditBtn}
                    />
                  </>
                ) : (
                  <></>
                )}
              </ViewerPanel>
            </ViewerContext.Provider>
          </PostLayout>

          {/* 우측 댓글 패널: 접기/열기 + 너비 조절 (자문 페이지 우측 창과 동일 UI).
              PostLayout 밖(오른쪽)에 두어 배치 모드와 무관하게 항상 뷰어 옆(우측)에 위치. */}
          {commentPanelOpen && (
            <CommentResizeDivider
              $active={isCommentResizing}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                setIsCommentResizing(true);
              }}
              style={viewerPanelHeight ? { height: `${viewerPanelHeight}px` } : undefined}
            >
              <CommentResizeHandle />
            </CommentResizeDivider>
          )}
          {commentPanelOpen ? (
            <CommentPanel
              style={{
                flexBasis: `${commentPanelWidth}px`,
                maxWidth: `${commentPanelWidth}px`,
                ...(viewerPanelHeight ? { height: `${viewerPanelHeight}px` } : {}),
              }}
            >
              <CommentPanelHeader>
                <CommentPanelTitle>
                  <CommentOutlined /> Comments ({comments.length})
                </CommentPanelTitle>
                <CommentCollapseBtn type="button" title="Comment 접기" onClick={() => setCommentPanelOpen(false)}>
                  <RightOutlined />
                </CommentCollapseBtn>
              </CommentPanelHeader>
              <CommentPanelBody>
                <CommentThread
                  comments={comments}
                  currentUser={commentUserName}
                  readOnly={!!partnerGuest}
                  onAdd={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                />
              </CommentPanelBody>
            </CommentPanel>
          ) : (
            <CommentCollapsedTab type="button" onClick={() => setCommentPanelOpen(true)} title="Comment 열기">
              <LeftOutlined />
              <CommentTabLabel>Comment</CommentTabLabel>
              <CommentOutlined />
            </CommentCollapsedTab>
          )}
          </PageBody>

          {/* === Modals === */}
          <AddImage
            postId={currentPostData.id}
            setImageList={setImageList}
            showAddImageModal={showAddImageModal}
            setShowAddImageModal={setShowAddImageModal}
            currentImage={currentImage}
            setCurrentImage={setCurrentImage}
            showEditImageModal={showEditImageModal}
            setShowEditImageModal={setShowEditImageModal}
            cases={cases}
            defaultCaseId={activeCaseId}
          />

          <AddSlide
            showUploadModal={showAddSlideModal}
            setShowUploadModal={setShowAddSlideModal}
            targetPostId={param.id ? +param.id : param.boardId ? +param.boardId : undefined}
            fileMode={searchParams.get('board') ? "all" : "slide"}
            cases={cases}
            defaultCaseId={activeCaseId}
            groups={groupsOf("SLIDE")}
            onGroupAssigned={reloadGroupsAndMedia}
          />

          <AddVideo
            postId={currentPostData.id}
            open={showAddVideoModal}
            setOpen={setShowAddVideoModal}
            cases={cases}
            defaultCaseId={activeCaseId}
            onUploaded={reloadVideoList}
          />

          <AddGrossImage
            postId={currentPostData.id}
            setImageList={setGrossImageList}
            showAddImageModal={showAddGrossImageModal}
            setShowAddImageModal={setShowAddGrossImageModal}
            currentImage={currentGrossImage}
            setCurrentImage={setCurrentGrossImage}
            showEditImageModal={showEditGrossImageModal}
            setShowEditImageModal={setShowEditGrossImageModal}
            cases={cases}
            defaultCaseId={activeCaseId}
          />

          <ImageEditModal
            open={imageListEditModalOpen}
            orderImageList={orderImageList}
            onOk={() => handleOrder("image")}
            onCancel={closeImageEditModal}
            onDragEnd={(e, selected) => handleOnDragEnd(e, "image", selected)}
            onDeleteImage={deleteImage}
            onDeleteMany={deleteManyImages}
            onConvertToGross={convertImagesToGross}
            cases={cases}
            onCaseAssigned={handleCaseAssigned}
            groups={groupsOf("PHOTO")}
            onAssignGroup={(ids, target, newName) => handleMoveToGroup("PHOTO", ids, target, newName)}
          />

          <SlideEditModal
            open={slideListEditModalOpen}
            orderSlideList={orderSlideList}
            onOk={() => handleOrder("slide")}
            onCancel={closeSlideEditModal}
            onDragEnd={(e, selected) => handleOnDragEnd(e, "slide", selected)}
            onDeleteSlide={deleteSlideItem}
            onDeleteMany={deleteManySlides}
            cases={cases}
            onCaseAssigned={handleCaseAssigned}
            groups={groupsOf("SLIDE")}
            onAssignGroup={(ids, target, newName) => handleMoveToGroup("SLIDE", ids, target, newName)}
          />

          <GrossImageEditModal
            open={grossImageListEditModalOpen}
            orderImageList={orderGrossImageList}
            onOk={() => handleOrder("gross")}
            onCancel={closeGrossImageEditModal}
            onDragEnd={(e, selected) => handleOnDragEnd(e, "gross", selected)}
            onDeleteImage={deleteGrossImageItem}
            onDeleteMany={deleteManyGrossImages}
            onConvertToPhoto={convertGrossToImages}
            cases={cases}
            onCaseAssigned={handleCaseAssigned}
            groups={groupsOf("GROSS")}
            onAssignGroup={(ids, target, newName) => handleMoveToGroup("GROSS", ids, target, newName)}
          />

          <PostCaseModal
            open={postCaseModalOpen}
            postId={currentPostData.id}
            editingCase={editingCase}
            nextListOrder={cases.length + 1}
            onClose={() => setPostCaseModalOpen(false)}
            onSuccess={handleCaseModalSuccess}
          />

          <PostCaseOrderModal
            open={caseOrderModalOpen}
            cases={cases}
            onCancel={() => setCaseOrderModalOpen(false)}
            onSubmit={handleReorderCases}
          />
        </PostPageWrapper>
      ) : accessDeniedMessage ? (
        <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Result
            status="403"
            title="접근 권한 없음"
            subTitle={accessDeniedMessage}
            extra={
              <Button type="primary" onClick={() => navigate("/")}>
                메인으로 돌아가기
              </Button>
            }
          />
        </Layout.Content>
      ) : !isSecretPost ? (
        <Layout.Content>
          <Layout style={{ ...BoxStyle, minHeight: ContentMinHeight }}>
            <Layout.Content>
              <List />
            </Layout.Content>
          </Layout>
        </Layout.Content>
      ) : null}

      <Modal
        title="비밀글 확인"
        open={isSecretPost}
        onOk={handleSecretSubmit}
        onCancel={handleSecretCancel}
        okText="확인"
        cancelText="뒤로 가기"
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <p style={{ marginBottom: 12 }}>이 게시글은 비밀글입니다. 열람하려면 비밀번호를 입력해주세요.</p>
          <Input.Password
            placeholder="숫자 4자리"
            maxLength={4}
            value={secretPassword}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
              setSecretPassword(onlyNumbers);
            }}
            onPressEnter={handleSecretSubmit}
          />
        </div>
      </Modal>

      {/* 실제 증례(OCSPST) 링크 추가/해제 모달 - 한 게시글에 여러 증례, 한 증례는 한 게시글에만 */}
      <Modal
        title="실제 증례와 링크 연결하기"
        open={linkModalOpen}
        onCancel={() => setLinkModalOpen(false)}
        footer={<Button onClick={() => setLinkModalOpen(false)}>닫기</Button>}
        centered
      >
        <div style={{ marginTop: 16, marginBottom: 8 }}>
          {ocspstLinks.length > 0 && (
            <OcspstLinkList>
              {ocspstLinks.map((link) => (
                <li key={link.ocspstLinkId}>
                  <div className="info">
                    <span className="case">{ocspstCaseLabel(link)}</span>
                    <a className="url" href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.url}
                    </a>
                  </div>
                  <Popconfirm
                    title="이 증례와의 링크를 해제할까요?"
                    okText="해제"
                    cancelText="취소"
                    onConfirm={() => handleRemoveOcspstLink(link)}
                  >
                    <Button size="small" danger>
                      해제
                    </Button>
                  </Popconfirm>
                </li>
              ))}
            </OcspstLinkList>
          )}
          <p style={{ marginBottom: 12, color: "#595959" }}>
            소아고형암 온라인 자문시스템의 실제 증례 URL 을 입력하세요. 여러 증례를 연결할 수 있지만, 한 증례는 한 게시글에만 연결됩니다.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              placeholder="http://pa.ccrdp.org/viewer?groupId=..&caseId=.."
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              onPressEnter={handleAddOcspstLink}
              allowClear
            />
            <Button type="primary" onClick={handleAddOcspstLink} loading={linkSaving} disabled={!linkInput.trim()}>
              추가
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const PostPageWrapper = styled(Layout.Content)<{ $navBarOffset: number }>`
  max-width: 100%;
  overflow-x: hidden;
  padding-top: ${({ $navBarOffset }) => $navBarOffset}px;
  transition: padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const BreadcrumbWrapper = styled.div`
  flex-shrink: 0;
`;

const PostLayout = styled(Layout)<{
  $layoutMode: "vertical" | "horizontal";
  $isResizing: boolean;
}>`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #ffffff;
  position: relative;
  display: flex;
  flex-direction: ${({ $layoutMode }) =>
    $layoutMode === "vertical" ? "column" : "row"};
  align-items: stretch;
  ${({ $isResizing }) =>
    $isResizing ? "" : "transition: all 0.2s ease;"}
`;

const LayoutToggleButton = styled(Button)`
  flex-shrink: 0;
`;

/* PostLayout(에디터/뷰어) + 우측 댓글 패널을 나란히 두는 flex row 래퍼 */
const PageBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  & > #post-layout-container {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

/* ── 우측 댓글 패널 (접기/열기, 너비는 인라인 flex-basis 로 지정) ── */
const CommentPanel = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  overflow: hidden;
  align-self: flex-start;
`;

/* 댓글 패널 너비 조절 바 */
const CommentResizeDivider = styled.div<{ $active: boolean }>`
  flex: 0 0 8px;
  align-self: flex-start;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $active }) => ($active ? "#1890ff" : "transparent")};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #1890ff;
  }
  &:hover > div,
  ${({ $active }) => $active && "& > div"} {
    background-color: #f7f7f7;
    opacity: 1;
  }
`;

const CommentResizeHandle = styled.div`
  width: 2px;
  height: 40px;
  background-color: #d9d9d9;
  border-radius: 1px;
  opacity: 0.6;
  transition: all 0.15s ease;
`;

const CommentPanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
`;

const CommentPanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e2e3a;
`;

const CommentCollapseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #8c8c8c;
  padding: 4px 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s;

  &:hover {
    color: #1890ff;
    background: #eef5ff;
  }
`;

const CommentPanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 2px 10px;
`;

const CommentCollapsedTab = styled.button`
  flex: 0 0 40px;
  align-self: stretch;
  border: none;
  border-left: 1px solid #e8e8e8;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  color: #595959;
  transition: all 0.15s;

  &:hover {
    color: #1890ff;
    background: #f0f5ff;
  }
`;

/* ── 좌측 본문 패널 접기 (댓글 패널과 같은 모양, 좌우만 반대) ── */
const ContentPanelHeader = styled(CommentPanelHeader)`
  margin: 0 -10px;
`;

const ContentCollapsedTab = styled(CommentCollapsedTab)`
  order: 1;
  border-left: none;
  border-right: 1px solid #e8e8e8;
`;

const CommentTabLabel = styled.span`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 1px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const ViewerPanel = styled.div<{ $layoutMode: "vertical" | "horizontal" }>`
  display: flex;
  flex-direction: column;
  ${({ $layoutMode }) =>
    $layoutMode === "horizontal"
      ? `
    order: 3;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
  `
      : `
    order: 1;
    width: 100%;
  `}
`;

const MediaTabWrapper = styled.div``;

const ContentPanel = styled.div<{
  $layoutMode: "vertical" | "horizontal";
  $width: number;
}>`
  ${({ $layoutMode, $width }) =>
    $layoutMode === "horizontal"
      ? `
    order: 1;
    flex: 0 0 ${$width}%;
    min-width: 0;
    max-width: ${$width}%;
    margin: 0;
    padding: 0 10px;
    overflow-x: hidden;
  `
      : `
    order: 2;
    margin: auto;
    max-width: 70%;
    width: 100%;
  `}
`;

const ResizeDivider = styled.div<{ $isResizing: boolean }>`
  order: 2;
  flex: 0 0 8px;
  cursor: col-resize;
  background-color: ${({ $isResizing }) =>
    $isResizing ? "#1890ff" : "transparent"};
  position: relative;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #1890ff;
  }

  &:hover > div,
  ${({ $isResizing }) => $isResizing && "& > div"} {
    background-color: #f7f7f7;
    opacity: 1;
  }
`;

const ResizeHandle = styled.div`
  width: 2px;
  height: 40px;
  background-color: #d9d9d9;
  border-radius: 1px;
  opacity: 0.6;
  transition: all 0.15s ease;
`;

const ContentSection = styled.section`
  margin: 10px auto 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

// 같은 카테고리 내 이전/다음 게시글 이동 바
const PostNavBar = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  padding: 0 5px;
`;

const PostNavButton = styled.button`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  color: #595959;
  cursor: pointer;
  transition: all 0.15s ease;
  overflow: hidden;

  &.next {
    justify-content: flex-end;
    text-align: right;
  }

  &:not(.next) {
    justify-content: flex-start;
  }

  &:hover:not(:disabled) {
    border-color: #1890ff;
    color: #1890ff;
    background: #f0f7ff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .anticon {
    flex-shrink: 0;
    font-size: 0.85rem;
  }
`;

const PostNavText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: 1.3;
  text-align: left;

  &.right {
    align-items: flex-end;
    text-align: right;
  }

  .dir {
    font-size: 0.72rem;
    font-weight: 600;
    color: #8c8c8c;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .title {
    font-size: 0.85rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PostMetaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 5px;
  color: #666;
  font-size: 0.9rem;
`;

// 우측: 작성자 / 최근 수정 그룹
const MetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

// 증례 링크 표시 이름 (OCSPST 증례 id)
const ocspstCaseLabel = (link: OcspstLink) => `증례 #${link.ocspstCaseId}`;

const OcspstLinkList = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  border: 1px solid #f0f0f0;
  border-radius: 8px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
  }
  li + li {
    border-top: 1px solid #f0f0f0;
  }

  .info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .case {
    font-weight: 600;
    color: #1e2e3a;
  }
  .url {
    font-size: 12px;
    color: #8c8c8c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// 좌측: 실제 증례(OCSPST) 링크 버튼 영역
const OcspstArea = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OcspstLinkButton = styled(Button)`
  font-weight: 500;
`;

const OcspstViewButton = styled(Button)`
  font-weight: 600;
  background-color: #2f5c8f;
  border-color: #2f5c8f;

  &:hover,
  &:focus {
    background-color: #3d6ea5 !important;
    border-color: #3d6ea5 !important;
  }
`;

const MetaText = styled.span<{ $isLink?: boolean }>`
  cursor: ${({ $isLink }) => ($isLink ? "pointer" : "default")};
  color: ${({ $isLink }) => ($isLink ? "#1890ff" : "inherit")};

  &:hover {
    text-decoration: ${({ $isLink }) => ($isLink ? "underline" : "none")};
  }
`;

const MetaDivider = styled.span`
  color: #d9d9d9;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px;

  .history-title {
    font-weight: bold;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    padding-bottom: 4px;
    margin-bottom: 2px;
  }

  .history-item {
    display: flex;
    justify-content: space-between;
    gap: 15px;
    font-size: 0.85rem;

    .editor { font-weight: 500; }
    .date { opacity: 0.8; }
  }
`;

const AttachmentSection = styled.section`
  margin: 30px auto 0;
  padding: 0 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const InfoPanelHeader = styled.div`
  position: relative;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  padding: 15px 0;
  border-bottom: 1px solid lightgray;
  flex-shrink: 0;
`;

const InfoPanelDivider = styled.hr`
  border: none;
  border-top: 1px solid lightgray;
  margin: 4px 0;
`;

export default Post;